// Service catalog for SEO landing pages.
// Greater London-only Peopels.com services.

export type ServiceSlug =
  | "handyman"
  | "tv-mounting"
  | "furniture-assembly"
  | "moving"
  | "furniture-removal"
  | "heavy-lifting"
  | "ikea-assembly"
  | "furniture-disassembly"
  | "shelf-mounting"
  | "picture-hanging"
  | "curtain-blind-installation"
  | "home-repairs"
  | "appliance-installation"
  | "door-repair"
  | "drywall-repair";

export type ServiceDef = {
  slug: ServiceSlug;
  name: string;
  shortName: string;
  rateLow: number;
  rateMid: number;
  rateHigh: number;
  avgDurationMinutes: number;
  summary: string;
  includes: string[];
  goodFor: string[];
  notIncluded: string[];
  /** Parent category slug used by booking/AI routing — maps each sub-service back to one of the four core categories. */
  parent: "handyman" | "tv-mounting" | "furniture-assembly" | "moving";
};

export const SERVICES: ServiceDef[] = [
  {
    slug: "handyman",
    name: "Handyman",
    shortName: "Handyman",
    parent: "handyman",
    rateLow: 30, rateMid: 45, rateHigh: 60, avgDurationMinutes: 90,
    summary: "Trusted local handymen for small repairs, fixings and odd jobs around the home.",
    includes: [
      "Hanging shelves, pictures and mirrors",
      "Small plumbing fixes (taps, traps, toilet flushes)",
      "Door, lock and handle adjustments",
      "Minor carpentry and wall repairs",
      "Light fitting swaps and curtain poles",
      "General odd jobs around the home",
    ],
    goodFor: ["Quick jobs under two hours", "Snags in a new flat", "End-of-tenancy fixes"],
    notIncluded: ["Full electrical rewires", "Gas work (must be Gas Safe registered)", "Major structural work"],
  },
  {
    slug: "tv-mounting",
    name: "TV Mounting",
    shortName: "TV Mounting",
    parent: "tv-mounting",
    rateLow: 35, rateMid: 50, rateHigh: 70, avgDurationMinutes: 75,
    summary: "Safe, professional TV wall mounting on plasterboard, brick and concrete walls.",
    includes: [
      "Bracket fitting on plasterboard, brick or concrete",
      "Cable concealment in trunking or behind plasterboard",
      "Soundbar and shelf mounting",
      "Soundbar, AV receiver and console set-up",
      "Wall-type assessment and safe fixings",
      "Removal and replacement of an old mount",
    ],
    goodFor: ["Mounting above a fireplace", "Mounting in a new-build flat", "Articulating arms and tilt mounts"],
    notIncluded: ["Supplying the TV bracket (bring your own)", "Aerial installation"],
  },
  {
    slug: "furniture-assembly",
    name: "Furniture Assembly",
    shortName: "Flat-pack",
    parent: "furniture-assembly",
    rateLow: 25, rateMid: 35, rateHigh: 50, avgDurationMinutes: 90,
    summary: "Fast, careful flat-pack assembly — IKEA, wardrobes, beds, desks and more.",
    includes: [
      "IKEA, Argos, Made.com and Wayfair flat-pack",
      "Wardrobes including PAX systems",
      "Beds, ottomans and bunk frames",
      "Desks, drawers and shelving units",
      "Disposal of packaging on request",
      "Wall-anchoring tall units for safety",
    ],
    goodFor: ["PAX wardrobes", "Bunk and ottoman beds", "Office desks and chairs"],
    notIncluded: ["Supplying the furniture", "Custom-made joinery"],
  },
  {
    slug: "moving",
    name: "Moving Help",
    shortName: "Moving",
    parent: "moving",
    rateLow: 25, rateMid: 40, rateHigh: 55, avgDurationMinutes: 120,
    summary: "Friendly man-and-van moving help across Greater London — single items to whole flats.",
    includes: [
      "One or two-person moving help",
      "Loading and unloading the van",
      "Stairs and walk-up flats",
      "Single-item collection and delivery",
      "Studio, 1-bed and 2-bed flat moves",
      "Furniture protection and basic strapping",
    ],
    goodFor: ["Studio and 1-bed moves", "Marketplace pick-ups", "Moving large single items"],
    notIncluded: ["Long-distance international removals", "Piano and safe specialist moves"],
  },
  {
    slug: "furniture-removal",
    name: "Furniture Removal",
    shortName: "Furniture Removal",
    parent: "moving",
    rateLow: 30, rateMid: 45, rateHigh: 65, avgDurationMinutes: 90,
    summary: "Bulky furniture collection and removal across Greater London — responsibly disposed where possible.",
    includes: [
      "Sofa, mattress and wardrobe removal",
      "Single-item or full-flat clearance",
      "Carry-down from upper floors and walk-ups",
      "Loading, strapping and safe transport",
      "Recycling or licensed waste disposal where available",
      "Same-week and next-day slots in most boroughs",
    ],
    goodFor: ["End-of-tenancy clearance", "Old sofa and mattress removal", "Replacing furniture on delivery day"],
    notIncluded: ["Hazardous waste (paint, chemicals, asbestos)", "Builders' rubble or large-scale construction waste"],
  },
  {
    slug: "heavy-lifting",
    name: "Heavy Lifting",
    shortName: "Heavy Lifting",
    parent: "moving",
    rateLow: 25, rateMid: 40, rateHigh: 60, avgDurationMinutes: 90,
    summary: "Extra hands for heavy, awkward or two-person lifts — without booking a full van move.",
    includes: [
      "Two-person lifts up and down stairs",
      "Moving heavy items between rooms",
      "Loading a van or rented vehicle you've hired",
      "Repositioning sofas, beds and wardrobes",
      "White-goods shuffle for kitchen fits",
      "Garden furniture and gym equipment",
    ],
    goodFor: ["Rearranging a room", "Loading a hire van", "Moving a heavy item up stairs"],
    notIncluded: ["Pianos and safes (specialist movers)", "Hot tubs and large garden machinery"],
  },
  {
    slug: "ikea-assembly",
    name: "IKEA Furniture Assembly",
    shortName: "IKEA Assembly",
    parent: "furniture-assembly",
    rateLow: 25, rateMid: 35, rateHigh: 50, avgDurationMinutes: 90,
    summary: "Specialists in IKEA flat-pack — PAX, MALM, KALLAX, BILLY and the rest, built right the first time.",
    includes: [
      "PAX wardrobe systems with internal fittings",
      "MALM, HEMNES and BRIMNES bedroom ranges",
      "KALLAX and BILLY storage units",
      "BEKANT and LINNMON desks and office systems",
      "Wall anchoring for tall units as standard",
      "Packaging flat-packed and tidied",
    ],
    goodFor: ["New IKEA bedroom set-ups", "Home office builds", "Multi-item IKEA deliveries"],
    notIncluded: ["Supplying the IKEA furniture", "Bespoke joinery modifications"],
  },
  {
    slug: "furniture-disassembly",
    name: "Furniture Disassembly",
    shortName: "Disassembly",
    parent: "furniture-assembly",
    rateLow: 25, rateMid: 35, rateHigh: 50, avgDurationMinutes: 75,
    summary: "Careful take-down of beds, wardrobes and flat-pack furniture for moves, sales or disposal.",
    includes: [
      "Beds, bunk frames and ottomans",
      "Wardrobes including PAX and built-in flat-pack",
      "Desks, drawers and shelving units",
      "Parts bagged and labelled for re-assembly",
      "Protective wrapping for transport",
      "Coordination with your moving day",
    ],
    goodFor: ["Moving day prep", "Selling or donating furniture", "Refreshing a room"],
    notIncluded: ["Disposal of dismantled items (add Furniture Removal)", "Bespoke or built-in joinery removal"],
  },
  {
    slug: "shelf-mounting",
    name: "Shelf Mounting",
    shortName: "Shelves",
    parent: "handyman",
    rateLow: 30, rateMid: 45, rateHigh: 60, avgDurationMinutes: 60,
    summary: "Straight, secure shelves on any London wall — plasterboard, brick or concrete.",
    includes: [
      "Floating, bracketed and corner shelves",
      "Heavy-duty book and storage shelves",
      "Wall-type assessment and correct fixings",
      "Spirit-levelled, evenly spaced installation",
      "Multiple shelves aligned across a wall",
      "Tidy-up after drilling",
    ],
    goodFor: ["Living room display shelves", "Kitchen and utility storage", "Kids' bedroom organisers"],
    notIncluded: ["Supplying the shelves and brackets", "Custom-built carpentry"],
  },
  {
    slug: "picture-hanging",
    name: "Picture Hanging",
    shortName: "Pictures",
    parent: "handyman",
    rateLow: 25, rateMid: 40, rateHigh: 55, avgDurationMinutes: 60,
    summary: "Perfectly aligned pictures, mirrors and gallery walls — hung straight, fixed to last.",
    includes: [
      "Single frames, mirrors and canvases",
      "Gallery walls with measured layouts",
      "Heavy mirror and artwork hanging",
      "Correct fixings for plasterboard or masonry",
      "Spirit-level alignment and spacing",
      "Stud-finding for safe heavy fixings",
    ],
    goodFor: ["New home moves", "Statement gallery walls", "Heavy mirrors over fireplaces"],
    notIncluded: ["Supplying frames or fixings of your choice", "Specialist museum hanging systems"],
  },
  {
    slug: "curtain-blind-installation",
    name: "Curtain & Blind Installation",
    shortName: "Curtains & Blinds",
    parent: "handyman",
    rateLow: 30, rateMid: 45, rateHigh: 60, avgDurationMinutes: 75,
    summary: "Curtain poles, tracks, Roman, roller and Venetian blinds — neatly fitted and tested.",
    includes: [
      "Curtain poles and tracks",
      "Roller, Roman, Venetian and vertical blinds",
      "Plasterboard and lintel-suitable fixings",
      "Bay window pole bending and fitting (when supplied)",
      "Removal and disposal of old fittings",
      "Cord-safety checks for child-safe installations",
    ],
    goodFor: ["New build flats", "Bay window curtains", "Bedroom blackout blinds"],
    notIncluded: ["Supplying curtains, blinds or poles", "Made-to-measure manufacturing"],
  },
  {
    slug: "home-repairs",
    name: "Home Repairs",
    shortName: "Home Repairs",
    parent: "handyman",
    rateLow: 30, rateMid: 50, rateHigh: 65, avgDurationMinutes: 120,
    summary: "Multi-task home repair visits — knock through your to-do list in a single booking.",
    includes: [
      "Combine multiple small jobs in one visit",
      "Squeaky doors, sticking windows and loose handles",
      "Minor plumbing and silicone re-seals",
      "Toilet seat and tap replacements",
      "Patching small holes and touch-up filling",
      "Tightening, refitting and adjusting fixtures",
    ],
    goodFor: ["End-of-tenancy snag lists", "Move-in fix-up days", "Quarterly home maintenance"],
    notIncluded: ["Gas work (Gas Safe required)", "Major electrical work", "Roofing and structural repairs"],
  },
  {
    slug: "appliance-installation",
    name: "Appliance Installation",
    shortName: "Appliance Install",
    parent: "handyman",
    rateLow: 35, rateMid: 50, rateHigh: 70, avgDurationMinutes: 90,
    summary: "Washing machines, dishwashers, hobs, hoods and freestanding white goods — fitted and tested.",
    includes: [
      "Washing machines and dishwashers (plumbed-in)",
      "Freestanding cookers and fridges",
      "Cooker hoods and extractors (non-gas)",
      "Removal and disposal of old appliances on request",
      "Test run and leak check after install",
      "Levelling and securing under-counter units",
    ],
    goodFor: ["New appliance delivery days", "Kitchen swaps and upgrades", "Rental property fit-outs"],
    notIncluded: ["Gas hob and gas cooker installation (Gas Safe only)", "Building or modifying kitchen units"],
  },
  {
    slug: "door-repair",
    name: "Door Repair",
    shortName: "Door Repair",
    parent: "handyman",
    rateLow: 35, rateMid: 50, rateHigh: 70, avgDurationMinutes: 90,
    summary: "Sticking, slamming, sagging or scratched — internal door fixes done properly.",
    includes: [
      "Re-hanging and re-aligning sagging doors",
      "Planing and trimming doors that catch",
      "Replacing hinges, handles and latches",
      "Adjusting strike plates and frames",
      "Door closer and stopper fitting",
      "Filling and finishing minor damage",
    ],
    goodFor: ["Doors that drag on carpet", "Loose or rattling handles", "Pre-tenancy inspections"],
    notIncluded: ["Full door replacement and frame rebuilds", "Fire-door certification work"],
  },
  {
    slug: "drywall-repair",
    name: "Drywall Repair",
    shortName: "Drywall Repair",
    parent: "handyman",
    rateLow: 35, rateMid: 50, rateHigh: 70, avgDurationMinutes: 120,
    summary: "Patch holes, cracks and damaged plasterboard — left smooth and paint-ready.",
    includes: [
      "Small hole and dent patches",
      "Plasterboard cut-and-fit patch repairs",
      "Filling, sanding and smoothing",
      "Corner bead and edge repairs",
      "Hairline crack treatment",
      "Paint-ready finish (painting not included)",
    ],
    goodFor: ["End-of-tenancy hole repairs", "Hidden screw and bracket holes", "Doorknob impact patches"],
    notIncluded: ["Full room re-skim or replastering", "Painting and decorating"],
  },
];

export function getServiceBySlug(slug: string): ServiceDef | undefined {
  return SERVICES.find((s) => s.slug === (slug.toLowerCase() as ServiceSlug));
}

export function serviceSlugs(): ServiceSlug[] {
  return SERVICES.map((s) => s.slug);
}

// Service FAQ pool — used for service + area pages. Insert area name with {{area}}.
export function serviceFaqs(serviceSlug: ServiceSlug, areaName: string): { q: string; a: string }[] {
  const common = [
    {
      q: `How quickly can I get a Helper in ${areaName}?`,
      a: `Most ${areaName} jobs are matched within a few minutes. You'll see vetted local Helpers, their reviews and a clear price estimate before you commit.`,
    },
    {
      q: `Are Peopels.com Helpers in ${areaName} vetted?`,
      a: `Yes. Every Helper accepted on Peopels.com passes ID verification and KYC checks. Your card is securely authorized at booking and funds are held in Stripe escrow until the job is done.`,
    },
    {
      q: `How does payment work in ${areaName}?`,
      a: `You see a price estimate before booking. Your card is authorized when you book, and funds are only released to your Helper after you confirm the job is complete. Payments are secured by Stripe.`,
    },
  ];

  switch (serviceSlug) {
    case "tv-mounting":
      return [
        ...common,
        { q: `Can you mount a TV above a fireplace in ${areaName}?`,
          a: `Yes — most homes in ${areaName} can take a TV above the fireplace using a tilt or full-motion bracket. Your Helper will check the wall type (brick, plasterboard or concrete) and use the right fixings.` },
        { q: `Will you conceal the cables?`,
          a: `Yes, cable concealment in trunking or behind plasterboard is included as standard. If you want fully recessed cabling, mention it when posting so the Helper brings the right tools.` },
        { q: `Do you supply the TV bracket?`,
          a: `Helpers don't supply brackets as standard so you can pick the right one for your TV. If you need help choosing, message your matched Helper after booking.` },
      ];

    case "furniture-assembly":
    case "ikea-assembly":
      return [
        ...common,
        { q: `How long does an IKEA PAX wardrobe take?`,
          a: `A typical 2-door PAX with internal fittings takes 90–150 minutes for one Helper. Larger 3 or 4-door systems can take longer — your Helper will give you a realistic estimate before starting.` },
        { q: `Will you remove the packaging?`,
          a: `Yes — your Helper can flat-pack and tidy the packaging. If you'd like it taken away, mention it when posting your job so the Helper brings the right vehicle.` },
        { q: `Do you anchor wardrobes to the wall?`,
          a: `Yes. Tall units are anchored to the wall as standard for safety, using the right fixings for plasterboard or brick.` },
      ];

    case "furniture-disassembly":
      return [
        ...common,
        { q: `Will you bag and label the parts?`, a: `Yes — fittings are bagged, labelled and stored with the panels so the item can be re-assembled later without missing pieces.` },
        { q: `Can disassembly be timed with my move?`, a: `Yes. Most ${areaName} customers book the disassembly visit for the morning of their move or the evening before.` },
        { q: `Do you take the dismantled furniture away?`, a: `Not as standard — add Furniture Removal at booking and your Helper can dispose of or recycle the items responsibly.` },
      ];

    case "moving":
      return [
        ...common,
        { q: `How big is the van for ${areaName} moves?`,
          a: `Most Helpers operate a long-wheel-base or Luton van, which fits a studio or 1-bedroom flat in one trip. For larger moves, post the rough volume so we can match a Helper with the right vehicle.` },
        { q: `Do you cover stairs and walk-up flats?`,
          a: `Yes — most ${areaName} jobs include stairs. Flag the floor number when posting so your Helper can plan and price accurately.` },
        { q: `Can you collect a single item from a marketplace?`,
          a: `Yes. Single-item collection and delivery is a popular ${areaName} job — share the pickup and drop-off postcodes when posting.` },
      ];

    case "furniture-removal":
      return [
        ...common,
        { q: `What happens to my old furniture?`, a: `Where the item is in usable condition, your Helper will offer reuse or recycling options. Anything beyond reuse is taken to a licensed waste site.` },
        { q: `Can you collect from a flat above the ground floor?`, a: `Yes — most ${areaName} removals involve stairs and walk-ups. Tell us the floor and lift access when posting and we'll match the right Helper.` },
        { q: `Do you remove mattresses?`, a: `Yes. Mattress removal is one of the most common ${areaName} jobs and is fully covered by this service.` },
      ];

    case "heavy-lifting":
      return [
        ...common,
        { q: `Can I book one Helper for a heavy lift?`, a: `Many heavy items only need an extra pair of hands. For very heavy items (sofas, large wardrobes), we recommend two Helpers — flag this when posting.` },
        { q: `Do you bring a van?`, a: `Heavy lifting is hands-only by default. If you also need transport, book Moving Help instead so we match a Helper with the right van for ${areaName}.` },
        { q: `Can you help load a van I've already hired?`, a: `Yes — loading and unloading hire vans is a common booking. Share the pickup time so we line up Helper availability.` },
      ];

    case "shelf-mounting":
      return [
        ...common,
        { q: `Will my plasterboard wall hold shelves?`, a: `Plasterboard can usually take shelves with the right fixings (toggle bolts or anchors). Your Helper will stud-find for heavier loads to fix into solid timber.` },
        { q: `Can you align several shelves on one wall?`, a: `Yes. Multi-shelf layouts are measured, spirit-levelled and evenly spaced so everything lines up.` },
        { q: `Do you supply the shelves?`, a: `No — please have the shelves and brackets ready. Your Helper brings the tools, drill bits and standard fixings.` },
      ];

    case "picture-hanging":
      return [
        ...common,
        { q: `Can you hang a heavy mirror over a fireplace?`, a: `Yes — heavy mirrors are stud-found and fixed with the right hardware for the wall type. Your Helper will assess the wall before drilling.` },
        { q: `Can you lay out a gallery wall?`, a: `Yes. Bring the frames and a rough idea of the layout — your Helper will measure, mark and align everything for you.` },
        { q: `Do you fill holes from old picture hooks?`, a: `Small holes can be filled and smoothed as part of the visit, ready for touch-up paint (paint not included).` },
      ];

    case "curtain-blind-installation":
      return [
        ...common,
        { q: `Can you fit blinds in a bay window?`, a: `Yes — bay window blinds and curtain poles are a common ${areaName} job. Have the made-to-measure parts ready and your Helper will fit them.` },
        { q: `Will you remove old fittings?`, a: `Yes. Removal of old poles, tracks or blinds is included, with holes filled where needed.` },
        { q: `Are installations child-safe?`, a: `Yes — corded blinds are fitted with cord cleats or safety devices in line with UK child-safety guidance.` },
      ];

    case "home-repairs":
      return [
        ...common,
        { q: `Can I combine several small fixes in one visit?`, a: `Yes — this is the main reason ${areaName} customers book Home Repairs. List everything when posting and your Helper will price the visit accordingly.` },
        { q: `What's not covered?`, a: `Gas work, major electrical, roofing and structural work are out of scope. Your Helper will tell you up-front if a job needs a certified specialist.` },
        { q: `Do you bring materials?`, a: `Helpers bring standard tools and basic consumables. Specific parts (handles, taps, fittings) should be supplied or agreed in advance.` },
      ];

    case "appliance-installation":
      return [
        ...common,
        { q: `Can you install a washing machine?`, a: `Yes. Washing machine and dishwasher install includes connection, levelling and a test cycle to check for leaks.` },
        { q: `Do you take the old appliance away?`, a: `Yes — appliance removal can be added at booking and the unit is disposed of or recycled responsibly.` },
        { q: `Do you fit gas hobs?`, a: `No — gas appliance installs must be done by a Gas Safe registered engineer. Electric hobs and induction hobs are covered.` },
      ];

    case "door-repair":
      return [
        ...common,
        { q: `My door drags on the carpet — can you fix it?`, a: `Yes. Doors that drag are usually re-hung, planed or have hinges re-set. Your Helper will pick the right fix on the visit.` },
        { q: `Can you replace handles and locks?`, a: `Yes — internal handles, latches and basic locks are replaced as part of this service. Have the new hardware ready before the visit.` },
        { q: `Do you fit fire doors?`, a: `Fire-door certification work is out of scope. We can adjust and rehang fire doors but cannot sign off the install.` },
      ];

    case "drywall-repair":
      return [
        ...common,
        { q: `Can you patch a fist-sized hole?`, a: `Yes. Holes up to roughly a tile in size are patched with a plasterboard insert, filled, sanded and left paint-ready.` },
        { q: `Will the repair be invisible?`, a: `Once filled, sanded and painted, well-done patches blend in. Painting isn't included — we leave the area smooth and primer-ready.` },
        { q: `Do you do full room re-skims?`, a: `No — large-scale plastering and re-skims are outside our service. We focus on patches, repairs and small areas.` },
      ];

    case "handyman":
    default:
      return [
        ...common,
        { q: `What kind of handyman jobs are most common in ${areaName}?`,
          a: `In ${areaName} we see lots of shelves, mirrors, curtain poles, door adjustments, small plumbing fixes and snagging in new-build flats.` },
        { q: `Can I book a Helper for less than an hour?`,
          a: `Most jobs are priced hourly with a one-hour minimum. If you only need 30 minutes, post it anyway — your Helper may be able to combine other small fixes in the same visit.` },
        { q: `Do you supply materials?`,
          a: `Helpers bring standard tools and basic consumables (screws, plugs). For specific items like brackets, paint or replacement parts, please supply them or ask your Helper to pick them up.` },
      ];
  }
}
